import { motion } from "framer-motion";
import { Sun, Flame, Users } from "lucide-react";

const ACCENT = "#FFC107";

const TILES = [
  {
    icon: Flame,
    title: "Alfresco Everything",
    copy: "Entertaining decks, outdoor kitchens and breezeways designed for 300 days of NSW sunshine.",
    img: "https://images.unsplash.com/photo-1600210492090-a159ffa3aeaf?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    alt: "Modern alfresco patio for outdoor entertaining",
    testId: "lifestyle-alfresco",
  },
  {
    icon: Sun,
    title: "Solar-Native Builds",
    copy: "Rooftop solar, battery-ready switchboards and passive-cooling design baked into every plan.",
    img: "https://images.pexels.com/photos/356036/pexels-photo-356036.jpeg?auto=compress&cs=tinysrgb&w=1200",
    alt: "Solar panels under an Australian sky",
    testId: "lifestyle-solar",
  },
  {
    icon: Users,
    title: "Community by Design",
    copy: "Parks, shared gardens and multicultural gathering spaces that turn streets into neighbourhoods.",
    img: "https://images.pexels.com/photos/9716228/pexels-photo-9716228.jpeg?auto=compress&cs=tinysrgb&w=1200",
    alt: "Aerial view of a green masterplanned community park",
    testId: "lifestyle-community",
  },
];

export const LifestyleHub = () => (
  <section id="lifestyle" className="px-6 sm:px-12 lg:px-20 py-28 lg:py-36 bg-[#111827]" data-testid="lifestyle-section">
    <div className="max-w-7xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8 }}
        className="mb-14"
      >
        <p className="font-display text-[11px] font-bold tracking-[0.35em] text-[#FFC107] mb-4">
          CHAPTER 04 — THE NSW WAY OF LIFE
        </p>
        <h2 className="font-display font-extrabold tracking-tighter text-4xl sm:text-5xl text-white leading-[1.02] max-w-3xl">
          We don't just build addresses.{" "}
          <em className="font-serif-lux italic font-semibold" style={{ color: ACCENT }}>
            We build the weekend.
          </em>
        </h2>
        <p className="mt-6 max-w-2xl text-sm sm:text-base text-white/60 leading-relaxed">
          Eucalyptus on the breeze, snags on the barbie, kids on the cul-de-sac 'til dusk. Every
          JAI HOMES community is shaped around outdoor living, sustainable energy and the
          multicultural spirit that makes NSW home for locals and new Australians alike.
        </p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {TILES.map((t, i) => {
          const Icon = t.icon;
          return (
            <motion.figure
              key={t.title}
              data-testid={t.testId}
              initial={{ opacity: 0, y: 36 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.12 }}
              className="group relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03]"
            >
              <div className="relative h-72 overflow-hidden">
                <img
                  src={t.img}
                  alt={t.alt}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0E17] via-transparent to-transparent" />
                <div
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{ background: "radial-gradient(70% 80% at 50% 100%, rgba(255,193,7,0.18), transparent 70%)" }}
                />
              </div>
              <figcaption className="p-7 border-t-2" style={{ borderTopColor: ACCENT }}>
                <Icon size={20} style={{ color: ACCENT }} className="mb-3" />
                <h3 className="font-display font-bold text-lg text-white">{t.title}</h3>
                <p className="mt-2 text-xs text-white/55 leading-relaxed">{t.copy}</p>
              </figcaption>
            </motion.figure>
          );
        })}
      </div>

      <motion.blockquote
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1, delay: 0.2 }}
        className="mt-20 max-w-4xl mx-auto text-center"
        data-testid="lifestyle-quote"
      >
        <p className="font-serif-lux italic text-2xl sm:text-4xl text-white/85 leading-snug">
          "A home should hold the heat of summer, the cool of the eucalypts, and every generation
          that gathers under its roof."
        </p>
        <footer className="mt-5 font-display text-[11px] font-bold tracking-[0.3em] text-[#FFC107]">
          — THE JAI HOMES DESIGN ETHOS
        </footer>
      </motion.blockquote>
    </div>
  </section>
);
